// Liste de camions d'un lot, collée depuis Excel ou saisie (lot 057).
//
// Excel copie les cellules séparées par des tabulations, une ligne par rangée.
// Les listes envoyées par un partenaire arrivent aussi avec des points-virgules,
// ou sous forme d'une simple colonne de plaques. Une première ligne de titres
// (« Tracteur », « Citerne », « Téléphone »…) règle l'ordre des colonnes ; sans
// titres, l'ordre est : tracteur, citerne, téléphone, nom, quantité.

/** Colonnes d'une ligne de camion, dans l'ordre de la saisie. */
export const COLONNES_CAMION = [
  'tractor_plate',
  'tank_plate',
  'driver_phone',
  'driver_name',
  'expected_quantity',
  'product',
];

/** Produits proposés pour un camion (codes du catalogue). */
export const PRODUITS_CAMION = ['GASOIL', 'SUPER', 'PETROLE'];

const ALIAS_PRODUITS = {
  GASOIL: ['gasoil', 'gazole', 'diesel', 'go', 'ago', 'gazoil'],
  SUPER: ['super', 'supercarburant', 'essence', 'sp95', 'sp98', 'pms', 'sansplomb', 'ordinaire'],
  PETROLE: ['petrole', 'petrolelampant', 'lampant', 'kerosene', 'dpk'],
};

/** Code produit d'un texte collé (« Gasoil », « GO », « Essence »…), '' si inconnu. */
export const codeProduit = (texte) => {
  const cle = sansAccents(texte).replace(/[^a-z0-9]/g, '');
  if (!cle) return '';
  const trouve = Object.entries(ALIAS_PRODUITS).find(([code, alias]) => cle === code.toLowerCase() || alias.includes(cle));
  return trouve ? trouve[0] : '';
};

// La citerne d'abord : « Plaque citerne » est une citerne, pas un tracteur.
const MOTIFS_TITRES = [
  ['tank_plate', /citerne|remorque|semi|tank/],
  ['product', /produit|carburant|product|fuel|gasoil|hydrocarbure/],
  ['tractor_plate', /tracteur|tete|plaque|immat|matricule|camion|cheval/],
  ['driver_phone', /tel|phone|mobile|contact|numero|gsm/],
  ['driver_name', /nom|chauffeur|driver|conducteur/],
  ['expected_quantity', /quantite|qte|volume|litre|capacite|\bl\b/],
];

const sansAccents = (texte) =>
  String(texte ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

const decouper = (ligne) => {
  if (ligne.includes('\t')) return ligne.split('\t');
  if (ligne.includes(';')) return ligne.split(';');
  return [ligne];
};

/** Colonne désignée par un titre, ou null si le texte n'est pas un titre connu. */
const colonneDuTitre = (titre) => {
  const texte = sansAccents(titre);
  if (!texte || /\d{4,}/.test(texte)) return null;
  const trouve = MOTIFS_TITRES.find(([, motif]) => motif.test(texte));
  return trouve ? trouve[0] : null;
};

/**
 * Ordre des colonnes si la première ligne est une ligne de titres (au moins un
 * titre reconnu et aucune cellule qui ressemble à une plaque ou à un numéro).
 */
const lireTitres = (cellules) => {
  const colonnes = cellules.map(colonneDuTitre);
  const reconnus = colonnes.filter(Boolean);
  if (reconnus.length === 0) return null;
  // « AB 1234 » n'est pas un titre : la première cellule d'une vraie ligne de
  // données porte des chiffres, un titre n'en porte pas.
  if (cellules.some((cellule) => /\d/.test(String(cellule)))) return null;
  return colonnes;
};

const nombre = (texte) => {
  const brut = String(texte ?? '').replace(/[\s ]/g, '').replace(',', '.');
  if (!brut) return '';
  const valeur = Number(brut);
  return Number.isFinite(valeur) ? valeur : String(texte).trim();
};

let compteur = 0;
/** Identifiant local d'une ligne de la grille (jamais envoyé au serveur). */
export const nouvelleLigneCamion = (valeurs = {}) => {
  compteur += 1;
  return {
    cle: `camion-${Date.now().toString(36)}-${compteur}`,
    tractor_plate: '',
    tank_plate: '',
    driver_phone: '',
    driver_name: '',
    expected_quantity: '',
    product: '',
    ...valeurs,
  };
};

/**
 * Lit un texte collé. Rend les lignes de camion (avec `cle` locale) ; les
 * lignes vides sont ignorées. Le contrôle des plaques et des quantités reste
 * au serveur, qui refuse une ligne illisible avec un message en français.
 */
export const analyserCollage = (texte) => {
  const lignes = String(texte ?? '')
    .split(/\r?\n/)
    .map((ligne) => ligne.replace(/ /g, ' '))
    .filter((ligne) => ligne.trim() !== '');
  if (lignes.length === 0) return [];

  const premieres = decouper(lignes[0]).map((cellule) => cellule.trim());
  const titres = lireTitres(premieres);
  const donnees = titres ? lignes.slice(1) : lignes;

  return donnees.map((ligne) => {
    const cellules = decouper(ligne).map((cellule) => cellule.trim());
    const valeurs = {};
    cellules.forEach((cellule, index) => {
      const colonne = titres ? titres[index] : COLONNES_CAMION[index];
      if (!colonne || !cellule) return;
      if (colonne === 'expected_quantity') valeurs[colonne] = nombre(cellule);
      else if (colonne === 'product') valeurs[colonne] = codeProduit(cellule);
      else valeurs[colonne] = cellule;
    });
    return nouvelleLigneCamion(valeurs);
  });
};

const vide = (ligne) => COLONNES_CAMION.every((colonne) => String(ligne?.[colonne] ?? '').trim() === '');

/** Lignes de la grille prêtes à partir : sans clé locale, sans ligne entièrement vide. */
export const lignesPourEnvoi = (lignes) =>
  (lignes || [])
    .filter((ligne) => !vide(ligne))
    .map((ligne) => {
      const corps = {
        tractor_plate: String(ligne.tractor_plate ?? '').trim(),
        tank_plate: String(ligne.tank_plate ?? '').trim() || undefined,
        driver_phone: String(ligne.driver_phone ?? '').trim() || undefined,
        driver_name: String(ligne.driver_name ?? '').trim() || undefined,
        product: String(ligne.product ?? '').trim() || undefined,
      };
      const quantite = ligne.expected_quantity;
      if (quantite !== '' && quantite !== null && quantite !== undefined) {
        corps.expected_quantity = typeof quantite === 'number' ? quantite : nombre(quantite);
      }
      return corps;
    });

/** Ligne sans plaque de tracteur : le serveur la refuserait. */
export const ligneSansPlaque = (ligne) => !vide(ligne) && !String(ligne.tractor_plate ?? '').trim();

/** Nombre de lignes à envoyer et nombre de lignes à corriger. */
export const resumeGrille = (lignes) => {
  const aEnvoyer = (lignes || []).filter((ligne) => !vide(ligne));
  return { total: aEnvoyer.length, sansPlaque: aEnvoyer.filter(ligneSansPlaque).length };
};
